import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private TOKEN_BUFFER_TIME = 5 * 60 * 1000;
  private TOKEN_EXPIRY_TIME = 60 * 60 * 1000;

  constructor() {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (
      request.url.includes('server-sent-events') &&
      request.method === 'GET' &&
      !request.url.includes('/eventrouter/')
    ) {
      return next.handle(request);
    }

    if (this.shouldRefreshToken(request)) {
      return this.handleTokenRefresh(request, next);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private shouldRefreshToken(request: HttpRequest<any>): boolean {
    const authHeader = request.headers.get('Authorization');
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return false;
    }

    const tokenTimestamp = sessionStorage.getItem('salesforceTokenTimestamp');
    if (!tokenTimestamp) {
      return false;
    }

    const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
    const needsRefresh = tokenAge >= this.TOKEN_EXPIRY_TIME - this.TOKEN_BUFFER_TIME;

    return needsRefresh;
  }

  private handleTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshSalesforceToken().pipe(
        switchMap((token: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);

          return next.handle(this.addTokenToRequest(request, token));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          console.error('Token refresh failed:', err);
          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenToRequest(request, token));
        }),
      );
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshSalesforceToken().pipe(
        switchMap((token: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);

          return next.handle(this.addTokenToRequest(request, token));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          console.error('Failed to refresh token after 401:', err);
          this.clearTokens();

          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenToRequest(request, token));
        }),
      );
    }
  }

  private refreshSalesforceToken(): Observable<string> {
    return new Observable((observer) => {
      const oktaToken = sessionStorage.getItem('oktaToken');

      if (!oktaToken) {
        console.error(new Error('Okta token not found'));
        return;
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${oktaToken}`,
        'api-key': environment.apigeeConfig.clientId,
        'X-Org-Id': environment.salesForceConfig.orgId,
      });

      const url = `${environment.apigeeConfig.domain}${environment.apigeeConfig.salesForceTokenEndpoint}`;

      fetch(url, { headers: headers as any })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (data?.accessToken) {
            sessionStorage.setItem('salesforceToken', data.accessToken);
            sessionStorage.setItem('salesforceTokenTimestamp', Date.now().toString());
            observer.next(data.accessToken);
            observer.complete();
          } else {
            observer.error(new Error('No access token in response'));
          }
        })
        .catch((error) => {
          console.error('Token refresh request failed:', error);
          observer.error(error);
        });
    });
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private clearTokens(): void {
    sessionStorage.removeItem('salesforceToken');
    sessionStorage.removeItem('salesforceTokenTimestamp');
  }
}
